import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { batch, useStore } from 'react-redux';
import { v4 as uuid } from 'uuid';
import * as actions from '~actions';
import { useAdyen, useDispatch, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { POSOrder, POSPayment } from '~reducers';
import { GIFTCARD_STATUS, ORDER_ORIGIN } from '~../__queries__/globalTypes';
import { GIFT_CARD_BY_SHORTCODE } from '~Queries/gift-cards';
import { GetGiftCardByShortCode, GetGiftCardByShortCodeVariables } from '~Queries/__queries__';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';
import { useExpressCart } from '../ExpressCartProvider';
import { useExpressProductLibrary } from '../ExpressProductLibraryProvider';
import { useExpressLoyalty } from '../ExpressLoyaltyProvider';
import { EPOS_CREATE_ORDER_EASY_ID } from './api/queries';
import { EposCreateOrderEasyId, EposCreateOrderEasyIdVariables } from './api/__queries__';

/**
 * Where the shopper is in the checkout.
 * - idle:     browsing, no order in Redux
 * - receipt:  order created, asking "receipt or not"
 * - paying:   payment requested, waiting on the terminal
 * - failed:   terminal declined / errored - offer a retry
 * - done:     paid, showing the order number
 */
export type ExpressOrderPhase = 'idle' | 'receipt' | 'paying' | 'failed' | 'done';

/** Why a gift card was turned down - the modal picks the text. */
export type GiftCardRejection =
  | 'NOT_FOUND'
  | 'INACTIVE'
  | 'EXPIRED'
  | 'INSUFFICIENT_BALANCE'
  | 'LOOKUP_FAILED'
  | 'UNAVAILABLE';

export type GiftCardResult =
  | { success: true }
  | { success: false; reason: GiftCardRejection; balance?: number };

interface ExpressOrderContextType {
  phase: ExpressOrderPhase;
  /** The Redux order id for this checkout, null while idle. */
  orderId: string | null;
  order: POSOrder | undefined;
  payment: POSPayment | undefined;
  /** Short order number, e.g. "042" - assigned shortly after the order is created. */
  orderNumber: string | null;
  /** Creates the order in Redux from the cart and moves to the receipt question. */
  begin: () => void;
  /** Records the receipt choice on the order and asks the terminal for the money. */
  startPayment: (wantsReceipt: boolean) => void;
  /** Drops the order again and goes back to browsing. Only while nothing is paid. */
  cancel: () => void;
  /**
   * Gives up on a payment the terminal never answered: tells the terminal to
   * abort and, failing that, marks the payment failed - either way the flow
   * lands in "failed" with its back and retry.
   */
  abortPayment: () => Promise<void>;
  /**
   * Pays the whole order with a Baemingo gift card instead of the terminal.
   * Chosen before any terminal payment starts. Checks the card first (exists,
   * active, not expired, balance covers the total - no part payments), then
   * records the receipt choice and charges the card.
   */
  payWithGiftCard: (shortcode: string, wantsReceipt: boolean) => Promise<GiftCardResult>;
  /** After a failed gift card: back to "card or gift card?" with the order kept. */
  reopenPaymentChoice: () => void;
  /** After "done": clears the cart and everything in Redux for this order. */
  finish: () => void;
}

const ExpressOrderContext = createContext<ExpressOrderContextType | undefined>(undefined);

export const useExpressOrder = (): ExpressOrderContextType => {
  const context = useContext(ExpressOrderContext);
  if (!context) {
    throw new Error('useExpressOrder must be used within an ExpressOrderProvider');
  }
  return context;
};

interface Props {
  venueId: string;
  children: ReactNode;
}

/**
 * The bridge between the express cart and the POS order/payment machinery in
 * Redux. It does exactly what the kiosk and QSR do - `initiateOrder`,
 * `addItemToOrder` per packaged item, `initiatePayment` - so BongMaster,
 * printing and the payment sagas see an ordinary order. Fields EPOS has no
 * source for yet (staff, beeper, loyalty card) are simply left out.
 */
export const ExpressOrderProvider: FC<Props> = ({ venueId, children }) => {
  const logger = useLogger('epos-order');
  const dispatch = useDispatch();
  const store = useStore<{ POSPayments: POSPayment[] }>();
  const [storage] = useStorage();
  const expressCart = useExpressCart();
  const productLibrary = useExpressProductLibrary();
  const expressLoyalty = useExpressLoyalty();
  const adyen = useAdyen();
  const apolloClient = useApolloClient();
  const giftCardsContext = useGiftCardsContext();

  const [phase, setPhase] = useState<ExpressOrderPhase>('idle');
  const [orderId, setOrderId] = useState<string | null>(null);

  const order = useSelector((state) =>
    orderId ? state.POSOrders.find((o) => o.id === orderId) : undefined,
  );
  const payment = useSelector((state) =>
    orderId ? state.POSPayments.find((p) => p.order === orderId) : undefined,
  );

  const [createOrderEasyId] = useMutation<EposCreateOrderEasyId, EposCreateOrderEasyIdVariables>(
    EPOS_CREATE_ORDER_EASY_ID,
  );

  /** Adyen when a terminal is paired, otherwise Zettle unless we're in DEV */
  const method = storage.adyenTerminals.length > 0 ? 'ADYEN' : 'IZETTLE';

  const begin = useCallback(() => {
    const id = uuid();
    logger.info('Creating express order', {
      orderId: id,
      itemCount: expressCart.packagedItems.length,
      serviceType: expressCart.serviceType,
    });

    dispatch(
      actions.initiateOrder({
        id,
        serviceType: expressCart.serviceType,
        origin: ORDER_ORIGIN.EXPRESS,
        defaultOrderStatus: productLibrary.defaultOrderStatus ?? undefined,
        method,
        orderNumber: '',
        comment: '',
        zoneId: storage.zoneId,
        desiredTime: expressCart.desiredTime || undefined,
        discountType: expressCart.appliedDiscount?.type ?? null,
        discountRate: expressCart.appliedDiscount?.value ?? null,
        userId: expressCart.userId,
      }),
    );
    // The order exists in the store now (dispatch is synchronous), so the lines can follow.
    expressCart.packagedItems.forEach((item) => dispatch(actions.addItemToOrder(id, item)));

    setOrderId(id);
    setPhase('receipt');
  }, [
    dispatch,
    expressCart.packagedItems,
    expressCart.serviceType,
    expressCart.desiredTime,
    expressCart.appliedDiscount,
    expressCart.userId,
    productLibrary.defaultOrderStatus,
    storage.zoneId,
    method,
  ]);

  /** Fetches the short order number as soon as the order exists. Empty string on failure moves the order on. */
  const requestedNumberForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!order || order.orderNumber || requestedNumberForRef.current === order.id) {
      return;
    }

    requestedNumberForRef.current = order.id;
    const id = order.id;

    createOrderEasyId({ variables: { venueId }, fetchPolicy: 'no-cache' })
      .then((result) => {
        const orderNumber = result.data?.createOrderEasyId.orderEasyId ?? '';
        dispatch(actions.updateOrder(id, { orderNumber }));
      })
      .catch((error: Error) => {
        logger.error('Failed to create order number', {
          orderId: id,
          errorMessage: error.message,
        });
        dispatch(actions.updateOrder(id, { orderNumber: '' }));
      });
  }, [order?.id, order?.orderNumber]);

  /** startPayment and payWithGiftCard await - they read these after, never a stale closure. */
  const latestRef = useRef({ phase, orderId });
  latestRef.current = { phase, orderId };

  /** Guards the async gap in startPayment against a double tap. */
  const startingPaymentRef = useRef(false);

  const startPayment = useCallback(
    async (wantsReceipt: boolean) => {
      if (!orderId || phase === 'paying' || startingPaymentRef.current) {
        return;
      }
      const amount = expressCart.total - expressCart.discount;
      // A zero payment completes on its own and leaves the payment machinery without an
      // order to work on - the cart shells never offer checkout for it, this is the backstop.
      if (amount <= 0) {
        logger.error('Refusing to start a payment for a zero amount', { orderId, amount });
        return;
      }
      startingPaymentRef.current = true;

      try {
        // A card tapped for loyalty pays without a second prompt; a prompt nobody answered is aborted.
        const cardIdentity = await expressLoyalty.prepareForPayment();

        /** The shopper may have backed out (or timed out) while the loyalty prompt was aborted. */
        if (latestRef.current.orderId !== orderId || latestRef.current.phase !== phase) {
          logger.info('Checkout moved on while preparing the payment - not starting it', {
            orderId,
          });
          return;
        }
        logger.info('Initiating express payment', {
          orderId,
          method,
          amount,
          wantsReceipt,
          hasAcquiredCard: Boolean(cardIdentity?.reference),
        });

        /** A retry after a failed gift card goes back to the terminal - the order says so again. */
        dispatch(actions.updateOrder(orderId, { noReceipt: !wantsReceipt, method }));
        dispatch(
          actions.initiatePayment(orderId, {
            method,
            canCancel: true,
            amount,
            meta: cardIdentity?.reference ? { cardIdentity } : undefined,
          }),
        );
        setPhase('paying');
      } finally {
        startingPaymentRef.current = false;
      }
    },
    [orderId, phase, method, expressCart.total, expressCart.discount, expressLoyalty, dispatch],
  );

  /**
   * Removes this order and every payment tied to it from Redux. The shared
   * payment instances assume both exist, so the payments are read straight
   * from the store (not a possibly stale selector) and everything is removed
   * in one batch - no render ever sees a payment without its order.
   */
  const dropOrder = useCallback(() => {
    if (orderId) {
      const payments = store.getState().POSPayments.filter((p) => p.order === orderId);
      logger.info('Dropping express order', { orderId, paymentIds: payments.map((p) => p.id) });
      batch(() => {
        payments.forEach((p) => dispatch({ type: 'REMOVE_POS_PAYMENT', id: p.id }));
        dispatch({ type: 'REMOVE_POS_ORDER', id: orderId });
      });
    }
    requestedNumberForRef.current = null;
    setOrderId(null);
  }, [orderId, store, dispatch]);

  const abortPayment = useCallback(async () => {
    if (phase !== 'paying' || !payment) {
      return;
    }
    const terminalId = payment.variant ?? storage.adyenTerminals[0]?.POIID;
    logger.warn('Shopper gave up on a payment with no response - aborting', {
      orderId,
      paymentId: payment.id,
      method: payment.method,
      terminalId,
    });

    // A running Adyen transaction is told to stop; the payment method then reports the failure itself.
    if (payment.method === 'ADYEN' && payment.meta.serviceId && terminalId) {
      try {
        const result = await adyen.abortPayment({ serviceId: payment.meta.serviceId, terminalId });
        logger.info('Terminal abort requested', {
          paymentId: payment.id,
          success: result.success,
        });
        if (result.success) {
          return;
        }
      } catch (error) {
        logger.error('Terminal abort threw', {
          paymentId: payment.id,
          reason: error instanceof Error ? error.message : String(error),
        });
      }
    }

    // No terminal to abort, or it did not take - fail the payment ourselves so the flow moves on.
    dispatch({ type: actions.POS_PAYMENT_FAILED, id: payment.id, error: 'Cancelled by shopper' });
  }, [phase, payment, orderId, storage.adyenTerminals, adyen, dispatch]);

  /** One gift card at a time - a second submit during the lookup is refused. */
  const giftCardBusyRef = useRef(false);

  const payWithGiftCard = useCallback(
    async (shortcode: string, wantsReceipt: boolean): Promise<GiftCardResult> => {
      const startedForOrder = latestRef.current.orderId;
      const startedInPhase = latestRef.current.phase;
      const amount = expressCart.total - expressCart.discount;
      /** Only before the terminal is asked (or after it failed) - never next to a live payment. */
      const canStart = startedInPhase === 'receipt' || startedInPhase === 'failed';
      if (
        !startedForOrder ||
        !canStart ||
        amount <= 0 ||
        giftCardBusyRef.current ||
        startingPaymentRef.current
      ) {
        return { success: false, reason: 'UNAVAILABLE' };
      }
      giftCardBusyRef.current = true;

      try {
        logger.info('Looking up gift card', { orderId: startedForOrder, shortcode, amount });

        let giftcard: GetGiftCardByShortCode['Giftcard'];
        try {
          const result = await apolloClient.query<
            GetGiftCardByShortCode,
            GetGiftCardByShortCodeVariables
          >({
            query: GIFT_CARD_BY_SHORTCODE,
            variables: { universeId: giftCardsContext.universeId, shortcode },
            fetchPolicy: 'network-only',
          });
          giftcard = result.data.Giftcard;
        } catch (error) {
          logger.warn('Gift card lookup failed', {
            shortcode,
            reason: error instanceof Error ? error.message : String(error),
          });
          return { success: false, reason: 'LOOKUP_FAILED' };
        }

        if (!giftcard) {
          logger.info('Gift card not found', { shortcode });
          return { success: false, reason: 'NOT_FOUND' };
        }
        const expired =
          giftcard.status === GIFTCARD_STATUS.EXPIRED ||
          (!!giftcard.expiresAt && new Date(giftcard.expiresAt).getTime() < Date.now());
        if (expired) {
          logger.info('Gift card expired', { shortcode, expiresAt: giftcard.expiresAt });
          return { success: false, reason: 'EXPIRED' };
        }
        if (giftcard.status !== GIFTCARD_STATUS.ACTIVE) {
          logger.info('Gift card not active', { shortcode, giftCardStatus: giftcard.status });
          return { success: false, reason: 'INACTIVE' };
        }
        if (giftcard.balance < amount) {
          logger.info('Gift card balance too low', {
            shortcode,
            balance: giftcard.balance,
            amount,
          });
          return { success: false, reason: 'INSUFFICIENT_BALANCE', balance: giftcard.balance };
        }

        /** No terminal payment follows - a loyalty prompt or held card goes back to the terminal. */
        try {
          await expressLoyalty.release();
        } catch (error) {
          logger.warn('Releasing the loyalty card before a gift card payment failed', {
            orderId: startedForOrder,
            reason: error instanceof Error ? error.message : String(error),
          });
        }

        /** The shopper may have backed out or timed out during the lookup. */
        const latest = latestRef.current;
        if (latest.orderId !== startedForOrder || latest.phase !== startedInPhase) {
          logger.info('Checkout moved on during the gift card lookup - not charging', {
            orderId: startedForOrder,
          });
          return { success: false, reason: 'UNAVAILABLE' };
        }

        logger.info('Initiating express gift card payment', {
          orderId: startedForOrder,
          amount,
          shortcode,
          wantsReceipt,
        });
        /** The receipt reads the payment type off the order, not the payment. */
        dispatch(
          actions.updateOrder(startedForOrder, {
            noReceipt: !wantsReceipt,
            method: 'BAEMINGO_GIFTCARD',
          }),
        );
        dispatch(
          actions.initiatePayment(startedForOrder, {
            method: 'BAEMINGO_GIFTCARD',
            canCancel: false,
            disableTip: true,
            amount,
            meta: { giftCardShortcode: shortcode },
          }),
        );
        setPhase('paying');
        return { success: true };
      } finally {
        giftCardBusyRef.current = false;
      }
    },
    [
      expressCart.total,
      expressCart.discount,
      apolloClient,
      giftCardsContext.universeId,
      expressLoyalty,
      dispatch,
    ],
  );

  const reopenPaymentChoice = useCallback(() => {
    if (phase !== 'failed') {
      return;
    }
    logger.info('Back to the payment method choice after a failed payment', { orderId });
    setPhase('receipt');
  }, [phase, orderId]);

  const cancel = useCallback(() => {
    if (phase === 'paying' || phase === 'done') {
      return;
    }
    logger.info('Express checkout cancelled', { orderId });
    dropOrder();
    setPhase('idle');
  }, [phase, orderId, dropOrder]);

  const finish = useCallback(() => {
    logger.info('Express order finished', { orderId });
    dropOrder();
    expressCart.clear();
    setPhase('idle');
  }, [orderId, dropOrder, expressCart.clear]);

  // Follow the terminal.
  useEffect(() => {
    if (phase !== 'paying' || !payment) {
      return;
    }
    if (payment.status === 'COMPLETED') {
      logger.info('Express payment completed', {
        orderId,
        paymentId: payment.id,
        method: payment.method,
      });
      setPhase('done');
    } else if (payment.status === 'FAILED') {
      logger.warn('Express payment failed', {
        orderId,
        paymentId: payment.id,
        reason: payment.message,
      });
      // The payment record is dropped so a retry starts clean; the order stays.
      dispatch({ type: 'REMOVE_POS_PAYMENT', id: payment.id });
      // The terminal no longer holds the tapped card - a retry must prompt for one again.
      if (expressCart.loyalty?.cardIdentity.reference) {
        expressCart.setLoyalty({
          ...expressCart.loyalty,
          cardIdentity: { ...expressCart.loyalty.cardIdentity, reference: undefined },
        });
      }
      setPhase('failed');
    }
  }, [phase, payment?.status]);

  const contextValue = useMemo<ExpressOrderContextType>(
    () => ({
      phase,
      orderId,
      order,
      payment,
      orderNumber: order?.orderNumber ? order.orderNumber : null,
      begin,
      startPayment,
      cancel,
      abortPayment,
      payWithGiftCard,
      reopenPaymentChoice,
      finish,
    }),
    [
      phase,
      orderId,
      order,
      payment,
      begin,
      startPayment,
      cancel,
      abortPayment,
      payWithGiftCard,
      reopenPaymentChoice,
      finish,
    ],
  );

  return (
    <ExpressOrderContext.Provider value={contextValue}>{children}</ExpressOrderContext.Provider>
  );
};

export default ExpressOrderProvider;
